import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import styles from "./orthodontics-for-all-ages.module.css";
import { HoverableImageLink } from "~/components/about-us/HoverableImageLink";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { Link } from "~/components/RouterLink";

type Localization = {
  title: string;
  introHeading: string;
  introFirstBeforePractice: string;
  introFirstAfterPractice: string;
  introSecondBeforeSocial: string;
  socialLinkText: string;
  introSecondAfterSocial: string;
  adults: string;
  teens: string;
  children: string;
};

const englishLocalization: Localization = {
  title: "Orthodontics for All Ages",
  introHeading: "All Ages Accepted",
  introFirstBeforePractice: "At ",
  introFirstAfterPractice:
    ", we believe everyone should be able to get quality orthodontic care and a beautifully-aligned smile.",
  introSecondBeforeSocial:
    "We're proud to offer a full range of orthodontics for all ages so we always welcome children, teens, and adults at our Miami office. Be sure to follow our ",
  socialLinkText: "social",
  introSecondAfterSocial: "for office updates and great patients.",
  adults: "Adults",
  teens: "Teens",
  children: "Children",
};

const spanishLocalization: Localization = {
  title: "Ortodoncia para todas las edades",
  introHeading: "Todas las edades aceptadas",
  introFirstBeforePractice: "En ",
  introFirstAfterPractice:
    ", creemos que todos deberían poder recibir atención ortodóntica de calidad y una sonrisa bellamente alineada.",
  introSecondBeforeSocial:
    "Nos enorgullece ofrecer una gama completa de ortodoncia para todas las edades, por lo que siempre damos la bienvenida a niños, adolescentes y adultos en nuestra oficina de Miami. Asegúrate de seguir nuestras ",
  socialLinkText: "redes sociales",
  introSecondAfterSocial:
    "para novedades de la oficina y publicaciones de nuestros excelentes pacientes.",
  adults: "Adultos",
  teens: "Adolescentes",
  children: "Niños",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function OrthodonticsForAllAgesPage() {
  const getLocalization = useLocalization(localized);
  return (
    <>
      <HeroStyle2
        heroImageUrl="/all-ages.jpg"
        title={getLocalization("title")}
        backgroundPosition="center"
      />
      <Flex
        className={`section ${styles.introSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex
          direction="column"
          className={`innerSection ${styles.contentText}`}
          p="5"
        >
          <Heading as="h2" className={styles.heading} weight="bold">
            {getLocalization("introHeading")}
          </Heading>
          <Text
            size="5"
            className={styles.paragraph}
            style={{
              lineHeight: "1.8em",
              letterSpacing: "normal",
              textWrap: "pretty",
              textAlign: "center",
            }}
          >
            {getLocalization("introFirstBeforePractice")}
            <Link to="/" className={styles.link}>
              Miami Orthodontic Specialists
            </Link>
            {getLocalization("introFirstAfterPractice")}
          </Text>
          <Text
            size="5"
            style={{
              lineHeight: "1.8em",
              letterSpacing: "normal",
              textWrap: "pretty",
              textAlign: "center",
            }}
          >
            {getLocalization("introSecondBeforeSocial")}
            <Link
              to="https://www.facebook.com/miamibraces/"
              className={styles.link}
            >
              {getLocalization("socialLinkText")}
            </Link>{" "}
            {getLocalization("introSecondAfterSocial")}
          </Text>
        </Flex>
      </Flex>
      <Flex
        className={`section ${styles.mainContentSectionOuter}`}
        width="100%"
        justify="center"
        align="center"
      >
        <Flex
          gap="7"
          direction="column"
          width="100%"
          px="4"
          className={styles.mainContentSection}
        >
          <Flex
            wrap="wrap"
            width="100%"
            gap="4"
            justify={{ initial: "center", md: "between" }}
            direction={{ initial: "column", md: "row" }}
          >
            <Box flexBasis={{ initial: "0", md: "32%" }}>
              <HoverableImageLink
                imageUrl="/miami-ortho-6.jpg"
                label={getLocalization("adults")}
                to="/orthodontics-for-adults"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "32%" }}>
              <HoverableImageLink
                imageUrl="/invisalign-for-teens.jpg"
                label={getLocalization("teens")}
                to="/orthodontics-for-teens"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "32%" }}>
              <HoverableImageLink
                imageUrl="/miami-ortho-4.jpg"
                label={getLocalization("children")}
                to="/orthodontics-for-children"
              />
            </Box>
          </Flex>
        </Flex>
      </Flex>
      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
